import type { CardChanges } from '../cards/cards';
import { loadDifficulty } from '../cards/difficulty';
import type { Level } from '../cards/difficulty';
import type { ShopCard } from '../cards/shop';
import type { BoutResult } from '../rules/types';

const difficulty = loadDifficulty();

/** What the player has earned: his coins and the punches he bought; and the level he fights on. */
export interface Progress {
  coins: number;
  readonly owned: Set<string>;
  level: string;
}

export function freshProgress(): Progress {
  return { coins: 0, owned: new Set(), level: difficulty.start.id };
}

export type Bought = 'bought' | 'owned' | 'poor';

/** Buys a punch if he has the coins and does not own it yet; otherwise changes nothing. */
export function buy(progress: Progress, shop: ShopCard, id: string): Bought {
  const item = shop.items.find((i) => i.id === id);
  if (!item) throw new Error(`The shop has no punch "${id}"`);
  if (progress.owned.has(id)) return 'owned';
  if (progress.coins < item.price) return 'poor';
  progress.coins -= item.price;
  progress.owned.add(id);
  return 'bought';
}

/** Coins for a bout on a level: a win, more for a knockout, a little for a draw, all times the level's; a loss costs. */
export function payout(result: BoutResult, shop: ShopCard, level: Level): number {
  if (result.winner === 'opponent') return -level.coinsLost;
  return onLevel(result.winner === 'player' ? shop.coinsForWin + (result.byKnockout ? shop.coinsForKnockout : 0) : shop.coinsForDraw, level);
}

/** The shop's coins as the level pays them. */
export function onLevel(coins: number, level: Level): number {
  return Math.round(coins * level.coinsTimes);
}

/** Pays or takes the coins, never below none, and says how many really changed hands. */
export function settle(progress: Progress, coins: number): number {
  const before = progress.coins;
  progress.coins = Math.max(0, before + coins);
  return progress.coins - before;
}

/** The part of the browser's storage the save needs; tests hand in their own. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * A JSON value kept in the browser between visits. One that cannot be read is put aside under its own key, never thrown
 * away, and the fresh value is used; a browser that refuses storage just means nothing is kept.
 */
class JsonSave<T> {
  constructor(
    private readonly storage: KeyValueStore,
    private readonly key: string,
    private readonly read: (data: unknown) => T | null,
    private readonly write: (value: T) => unknown,
    private readonly fresh: () => T,
  ) {}

  load(): T {
    let text: string | null;
    try {
      text = this.storage.getItem(this.key);
    } catch {
      return this.fresh();
    }
    if (text === null) return this.fresh();
    const value = parseJson(text, this.read);
    if (value !== null) return value;
    try {
      this.storage.setItem(`${this.key}.broken`, text);
    } catch {
      // Nowhere to put it aside: the fresh start still stands.
    }
    return this.fresh();
  }

  save(value: T): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(this.write(value)));
    } catch {
      // Storage refused (a private window, a full disk): the game goes on, only unsaved.
    }
  }
}

/** The player's coins and punches. */
export class ProgressStore extends JsonSave<Progress> {
  static readonly key = 'boxing-ultimate-fight.progress';

  constructor(storage: KeyValueStore) {
    super(storage, ProgressStore.key, readProgress, (progress) => ({ coins: progress.coins, owned: [...progress.owned], level: progress.level }), freshProgress);
  }
}

/** Sashko's changes to the cards; the cards themselves check the numbers when they are loaded. */
export class TuningStore extends JsonSave<CardChanges> {
  static readonly key = 'boxing-ultimate-fight.cards';

  constructor(storage: KeyValueStore) {
    super(storage, TuningStore.key, (data) => (isPlainObject(data) ? (data as CardChanges) : null), (changes) => changes, () => ({}));
  }
}

function parseJson<T>(text: string, read: (data: unknown) => T | null): T | null {
  try {
    return read(JSON.parse(text));
  } catch {
    return null;
  }
}

function isPlainObject(data: unknown): boolean {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

function readProgress(data: unknown): Progress | null {
  if (!isPlainObject(data)) return null;
  const { coins, owned, level } = data as { coins?: unknown; owned?: unknown; level?: unknown };
  if (!Number.isInteger(coins) || (coins as number) < 0) return null;
  if (!Array.isArray(owned) || !owned.every((id) => typeof id === 'string')) return null;
  // A save from before the levels, or with a level there is no more: his coins are still his.
  const known = difficulty.levels.some((l) => l.id === level);
  return { coins: coins as number, owned: new Set(owned as string[]), level: known ? (level as string) : difficulty.start.id };
}
